<script setup lang="ts">
import { useAttrs } from 'vue'
import { useVModel } from '@vueuse/core'
import { cn } from '@/lib/utils'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<{
  defaultValue?: string | number
  modelValue?: string | number
  type?: string
  leading?: boolean
  trailing?: boolean
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number): void
}>()

const { class: className, ...rest } = useAttrs()

const modelValue = useVModel(props, 'modelValue', emits, {
  passive: true,
  defaultValue: props.defaultValue,
})
</script>

<template>
  <div class="relative w-full">
    <div 
      v-if="props.leading"
      class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
    >
      <slot name="leading">
        <span class="text-muted-foreground">
          <slot name="leading-content" />
        </span>
      </slot>
    </div>

    <input 
      v-model="modelValue" 
      :type="props.type" 
      :class="cn(
        // Base styles
        'flex h-10 w-full rounded-md px-3 py-2 text-sm',
        'bg-background text-foreground',
        // Border styles - more prominent
        'border-2 border-input/60',
        'transition-all duration-200 ease-in-out',
        // Focus styles - prominent primary colors
        'focus:border-primary focus:outline-none',
        'focus:ring-2 focus:ring-primary/20',
        'focus:ring-offset-1 focus:ring-offset-background',
        // Hover styles
        'hover:border-input',
        // File input styles
        'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
        // Placeholder styles
        'placeholder:text-muted-foreground/60',
        // Disabled styles
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted/50',
        // Error states (can be enhanced later)
        'invalid:border-destructive invalid:ring-destructive/20',
        // Leading/trailing padding adjustments
        props.leading && 'pl-10',
        props.trailing && 'pr-10',
        className ?? ''
      )" 
      v-bind="rest"
    >

    <div 
      v-if="props.trailing"
      class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
    >
      <slot name="trailing">
        <span class="text-muted-foreground">
          <slot name="trailing-content" />
        </span>
      </slot>
    </div>
  </div>
</template>